/**
 * KHO Reference Pages dùng chung (canonical) — 1 key/page cho toàn hệ thống.
 * Inbound/Outbound (và sau này Purchase/Sale Contract) chỉ "call" tới key trong kho.
 *
 * Cách build (an toàn, không chép lại cột): REMAP các định nghĩa page sẵn có từ
 * config từng function sang canonical key. 7 page shared → dùng bản INBOUND làm
 * chuẩn (unify triệt để, theo quyết định). 5 page riêng Outbound thêm từ config
 * Outbound. Label/filter override ở đây (vd calendar → "Calendar"; virtual-warehouse
 * + futures-allocation bật lọc ngày cả 2 bên).
 */
import { PROCUREMENT_REFERENCE_PAGE_CONFIGS as INB } from './inboundReferenceConfig'
import { SALE_CONTROL_REFERENCE_PAGE_CONFIGS as OUTB } from './outboundReferenceConfig'
// Purchase Contract + Sale Contract giữ config ở pages/ (còn importer khác) — kho
// remap sang canonical. Cast vì 2 config này dùng type riêng (shape tương thích).
import { PURCHASE_REFERENCE_PAGE_CONFIGS } from '@/pages/purchase/purchaseReferenceConfig'
import { SALE_REFERENCE_PAGE_CONFIGS } from '@/pages/saleContract/saleReferenceConfig'

import type { ReferenceFilterKind, ReferencePageConfig } from './types'

const PC = PURCHASE_REFERENCE_PAGE_CONFIGS as unknown as Record<string, ReferencePageConfig>
const SC = SALE_REFERENCE_PAGE_CONFIGS as unknown as Record<string, ReferencePageConfig>

export type ReferencePageKey =
  // shared (7) — bản Inbound làm chuẩn
  | 'virtual-warehouse'
  | 'calendar'
  | 'certification'
  | 'delivery-place'
  | 'cp-list'
  | 'list-and-valo'
  | 'futures-allocation'
  // inbound-only (9)
  | 'contract-statistics'
  | 'pending-payment'
  | 'received'
  | 'consignment'
  | 'ptbf-fixation-purchase'
  | 'cs-fixation'
  | 'cp-bank'
  | 'bank'
  | 'package'
  // outbound-only (5)
  | 'service-invoices'
  | 'value-updates'
  | 'amendment'
  | 'delivery'
  | 'ptbf-fixation-sale'
  // contract-only (7) — Purchase Contract + Sale Contract riêng
  | 'delivery-term'
  | 'futures'
  | 'license'
  | 'futures-lot-adjust'
  | 'certification-updates'
  | 'claims'
  | 'consignee'

type CatalogEntry = ReferencePageConfig<ReferencePageKey>

// canonical → (source config, old key). Label/filter override optional.
type Src = { cfg: Record<string, ReferencePageConfig>; old: string; label?: string; filters?: ReferenceFilterKind[] }

const SRC: Record<ReferencePageKey, Src> = {
  // ── shared: bản Inbound ──
  'virtual-warehouse': { cfg: INB, old: 'VirtualWarehouse', filters: ['date'] },
  'calendar': { cfg: INB, old: 'FuturesList', label: 'Calendar' },
  'certification': { cfg: INB, old: 'Certification' },
  'delivery-place': { cfg: INB, old: 'DeliveryPlace' },
  'cp-list': { cfg: INB, old: 'CPList' },
  'list-and-valo': { cfg: INB, old: 'ListandValo' },
  'futures-allocation': { cfg: INB, old: 'FuturesAllocation', filters: ['date'] },
  // ── inbound-only ──
  'contract-statistics': { cfg: INB, old: 'ContractStatistics', filters: ['date', 'forcedClose'] },
  'pending-payment': { cfg: INB, old: 'PendingPayment', filters: ['date', 'forcedClose'] },
  'received': { cfg: INB, old: 'Received' },
  'consignment': { cfg: INB, old: 'Consignment' },
  'ptbf-fixation-purchase': { cfg: INB, old: 'PTBFFixation' },
  'cs-fixation': { cfg: INB, old: 'CSFixation' },
  'cp-bank': { cfg: INB, old: 'CPBank' },
  'bank': { cfg: INB, old: 'Bank' },
  'package': { cfg: INB, old: 'Package' },
  // ── outbound-only ──
  'service-invoices': { cfg: OUTB, old: 'ServiceInvoices' },
  'value-updates': { cfg: OUTB, old: 'ValueUpdates' },
  'amendment': { cfg: OUTB, old: 'Annex', filters: ['date', 'finalized'] },
  'delivery': { cfg: OUTB, old: 'Delivery', filters: ['date'] },
  'ptbf-fixation-sale': { cfg: OUTB, old: 'Fixation', filters: ['date', 'finalized'] },
  // ── contract-only: Purchase Contract ──
  'delivery-term': { cfg: PC, old: 'Delivery terms' },
  'futures': { cfg: PC, old: 'Futures' },
  'license': { cfg: PC, old: 'License' },
  'futures-lot-adjust': { cfg: PC, old: 'FuturesLotAdjust' },
  // ── contract-only: Sale Contract ──
  'certification-updates': { cfg: SC, old: 'CertificationUpdates' },
  'claims': { cfg: SC, old: 'Claims' },
  'consignee': { cfg: SC, old: 'Consignee' },
}

function build(): Record<ReferencePageKey, CatalogEntry> {
  const out = {} as Record<ReferencePageKey, CatalogEntry>
  for (const key of Object.keys(SRC) as ReferencePageKey[]) {
    const { cfg, old, label, filters } = SRC[key]
    const base = cfg[old]
    if (!base) throw new Error(`Reference catalog: missing source page "${old}"`)
    out[key] = { ...base, pageKey: key, label: label ?? base.label, filters }
  }
  return out
}

/** Kho chung: canonical key → định nghĩa page (label + columns + filters). */
export const REFERENCE_CATALOG: Record<ReferencePageKey, CatalogEntry> = build()

/** Thứ tự page mỗi function "call" (canonical). Bám đúng Access combo order cũ. */
export const INBOUND_REFERENCE_ORDER: ReferencePageKey[] = [
  'contract-statistics',
  'pending-payment',
  'virtual-warehouse',
  'received',
  'consignment',
  'ptbf-fixation-purchase',
  'cs-fixation',
  'cp-list',
  'cp-bank',
  'futures-allocation',
  'list-and-valo',
  'calendar',
  'bank',
  'package',
  'delivery-place',
  'certification',
]

export const OUTBOUND_REFERENCE_ORDER: ReferencePageKey[] = [
  'service-invoices',
  'virtual-warehouse',
  'calendar',
  'value-updates',
  'amendment',
  'delivery',
  'list-and-valo',
  'delivery-place',
  'futures-allocation',
  'certification',
  'ptbf-fixation-sale',
  'cp-list',
]

export const PURCHASE_CONTRACT_REFERENCE_ORDER: ReferencePageKey[] = [
  'value-updates',
  'calendar',
  'cp-list',
  'delivery-term',
  'futures',
  'bank',
  'license',
  'futures-lot-adjust',
  'list-and-valo',
  'package',
  'delivery-place',
  'certification',
]

export const SALE_CONTRACT_REFERENCE_ORDER: ReferencePageKey[] = [
  'value-updates',
  'certification-updates',
  'claims',
  'package',
  'list-and-valo',
  'delivery-term',
  'futures-allocation',
  'cp-list',
  'consignee',
  'certification',
  'calendar',
  'delivery-place',
  'futures-lot-adjust',
]
